import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { SoftwareOptionsService } from '@core/companySoftwareOptions/service/softwareOptions.service';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';
import { Utils } from '@shared/utils.service';
import { FaceliftStore } from './facelift.store';

@Injectable({ providedIn: 'root' })
export class UiFaceliftService {
    // Prevents multiple subscriptions when initOrUpdateFaceliftAvailability is called more than once.
    private initialized = false;

    constructor(
        private readonly faceliftStore: FaceliftStore,
        private readonly shellContextService: ShellContextService,
        private readonly softwareOptionsService: SoftwareOptionsService,
        private readonly utils: Utils
    ) { }

    initOrUpdateFaceliftAvailability(): void {
        if (this.initialized) {
            return;
        }
        this.initialized = true;
        // Subscribes permanently — re-evaluates on every company switch.
        this.isFaceliftEnabled$.subscribe((isFaceliftEnabled) => {
            this.faceliftStore.update({ isFaceliftEnabled });
        });
    }

    private get isFaceliftEnabled$(): Observable<boolean> {
        return this.shellContextService.selectedCompanyId$.pipe(
            filter((companyId) => companyId !== 0),
            distinctUntilChanged(),
            switchMap((companyId) =>
                // catchError is scoped to the inner observable so an HTTP failure on one
                // company switch does not terminate the outer subscription and break all
                // subsequent company switches.
                this.softwareOptionsService.getCompanySoftwareOptionsKeys(companyId, true).pipe(
                    catchError(() => of([]))
                )
            ),
            map((softwareOptions) => {
                if (softwareOptions) {
                    const isHostedOnScanner = this.utils.isScanner();
                    const isFaceliftEnabledForScanner = softwareOptions.includes('cswo_EnableWebUiFaceliftScanner') && isHostedOnScanner;
                    const isFaceliftEnabledForMyitero = softwareOptions.includes('cswo_EnableWebUiFaceliftMyitero');
                    return isFaceliftEnabledForMyitero || isFaceliftEnabledForScanner;
                }
                return false;
            })
        );
    }
}
